import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Input, Button, Alert, Spinner } from '@/components/ui';
import { Mail, ArrowLeft, CheckCircle2 } from 'lucide-react';

export default function ForgotPassword() {
  const { resetPassword } = useAuth();

  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess(false);
    setLoading(true);

    try {
      await resetPassword(email);
      setSuccess(true);
    } catch (err: any) {
      console.error('Password reset error:', err);
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const getErrorMessage = (error: any): string => {
    const code = error.code || '';
    if (code === 'auth/user-not-found') {
      return 'No account found with this email address';
    }
    if (code === 'auth/invalid-email') {
      return 'Invalid email address';
    }
    if (code === 'auth/too-many-requests') {
      return 'Too many requests. Please try again later';
    }
    return error.message || 'An error occurred while sending reset email';
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-white py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full">
        {/* Logo */}
        <img src="/logo.png" alt="simcheck.ai" className="w-14 h-14 object-contain mb-6 mx-auto" />

        {success ? (
          /* Success State */
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-emerald-50 mb-6">
              <CheckCircle2 className="h-10 w-10 text-emerald-600" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Check your email
            </h2>
            <p className="text-sm text-gray-500 mb-8">
              We've sent a password reset link to{' '}
              <span className="font-medium text-gray-900">
                {email}
              </span>
              . Click the link in the email to reset your password.
            </p>
            <div className="space-y-3">
              <Alert variant="default">
                <p className="text-sm">
                  Didn't receive the email? Check your spam folder or{' '}
                  <button
                    onClick={() => setSuccess(false)}
                    className="font-medium text-accent-blue hover:brightness-110 underline"
                  >
                    try again
                  </button>
                  .
                </p>
              </Alert>
              <Link to="/login">
                <Button variant="secondary" className="w-full">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to sign in
                </Button>
              </Link>
            </div>
          </div>
        ) : (
          /* Form State */
          <>
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-2">
                Reset your password
              </h2>
              <p className="text-sm text-gray-500">
                Enter your email address and we'll send you a link to reset your password
              </p>
            </div>

            {/* Error Alert */}
            {error && (
              <div className="mb-6">
                <Alert variant="destructive">
                  {error}
                </Alert>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <Input
                label="Email address"
                type="email"
                name="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                disabled={loading}
              />

              <div className="space-y-3">
                <Button
                  type="submit"
                  disabled={loading}
                  variant="default"
                  className="w-full"
                >
                  {loading ? (
                    <Spinner size="sm" />
                  ) : (
                    <>
                      <Mail className="w-4 h-4 mr-2" />
                      Send reset link
                    </>
                  )}
                </Button>

                <Link to="/login">
                  <Button variant="ghost" className="w-full">
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back to sign in
                  </Button>
                </Link>
              </div>
            </form>

            <div className="mt-6 text-center">
              <p className="text-xs text-gray-400">
                Don't have an account?{' '}
                <Link
                  to="/signup"
                  className="font-medium text-primary hover:text-primary/80"
                >
                  Sign up
                </Link>
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

